"use client";

import React, { useState } from "react";
import { SecondaryPagination, Typography } from "@aph/ui-lib-humanx";
import { useRouter, useSearchParams } from "next/navigation";
import Bookmark from "@/components/BookmarkAction";
import Breadcrumbs from "@/components/Breadcrumbs";
import NavBar from "@/components/Header/NavBar";
import { getNavBarInfoForCategoryRouting } from "@/helpers";
import VideoCard from "@/components/PageVideoListing/Components/VideoCard";
import {
  HOME_PAGE_TITLE,
  NON_ONBOARDED_STATUS,
  ONBOARDED_HOME_PAGE_TITLE,
  ONBOARDING_STATUS,
  TYPE_VIDEO,
} from "@/helpers/constants";
import routes from "@/helpers/router";
import { DeviceInfo } from "@/interface/deviceInfo";
import SegmentInfoSelector from "@/redux/slice/auth/selectors/userSegmentInfo";
import { useAppSelector } from "@/redux/hooks";
import { assetClickedEvent } from "@/helpers/TSDEvents";
import FilterComponent from "@/components/FilterComponent";
import { FiltersApiResponse } from "@/components/PageAllResources/api/interface";
import { getFilterName } from "@/components/FilterComponent/helpers";
import styles from "./PageVideoListing.module.scss";

interface VideoListingPageProps {
  ua: DeviceInfo;
  userOnBoarded: boolean;
  videosData: any;
  pageNo: number;
  navBarList: any;
  filterData: FiltersApiResponse;
  urlFilter: any;
}

const VideoListingPage: React.FC<VideoListingPageProps> = (props) => {
  const { videosData, ua, userOnBoarded, navBarList, filterData: filters, urlFilter } = props;

  const videoList = videosData?.data;
  const paginationInfo = videosData?.meta?.pagination;

  const router = useRouter();
  const searchParams = useSearchParams();
  const queryParams = searchParams?.toString();

  const SegmentInfoObject = useAppSelector(SegmentInfoSelector);

  const resource = "video";
  const pathname: string = `${routes.resourcesListing(urlFilter, "video")}`;

  const navBarInfo = getNavBarInfoForCategoryRouting(navBarList?.data);

  const filterName = getFilterName(urlFilter, filters);

  const pageTitle = `All${filterName ? ` ${filterName}` : ""} ${resource}s`;

  const breadCrumbsArray = [
    {
      label: userOnBoarded ? ONBOARDED_HOME_PAGE_TITLE : HOME_PAGE_TITLE,
      url: routes.home,
    },
    {
      label: filterName || "All Resources",
      url: routes.resourcesListing(urlFilter),
    },
    {
      label: "All Videos",
      url: pathname,
    },
  ];

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const onPageChange = (pageNumber: number) => {
    const url = routes.resourcesListing(urlFilter, "video", pageNumber);
    router.push(`${url}${queryParams ? `?${queryParams}` : ""}`);
  };

  return (
    <>
      <NavBar
        navBarData={{ navBarList: navBarInfo }}
        userOnBoarded={userOnBoarded}
        userAgent={ua}
        pathname={pathname}
      />
      <div className="container">
        <div className={`breadcrumb`}>
          <Breadcrumbs options={breadCrumbsArray} />
        </div>
        <div className={styles.titleWrapper}>
          <Typography component="h1" className={`${styles.title}`}>
            {pageTitle}
          </Typography>
          <Typography className={styles.filterText} onClick={() => setIsFilterOpen(true)}>
            <span className={"icon-icon-filter"}></span>
          </Typography>
        </div>

        <div className={` ${styles.listingWrapper}`}>
          {videoList?.map((videoData, index) => {
            const { Slug, Title, Video, Tags, AuthorName } = videoData;
            const redirectUrl = routes.video(Slug);

            return (
              <VideoCard
                thumbNailImage={`${Video?.data?.attributes?.url}/ik-thumbnail.jpg`}
                tags={Tags}
                title={Title}
                AuthorName={AuthorName}
                userAgent={ua}
                redirectUrl={redirectUrl}
                onClick={() => {
                  assetClickedEvent({
                    asset_name: pageTitle,
                    asset_display_text: Title,
                    asset_position: 1,
                    asset_element_position: index + 1,
                    momverse_stage: SegmentInfoObject?.type
                      ? (ONBOARDING_STATUS[SegmentInfoObject?.type] ?? "")
                      : NON_ONBOARDED_STATUS,
                  });
                  router.push(redirectUrl);
                }}
                key={Slug}
                isImageEagerload={ua?.isDesktop ? index <= 3 : index <= 1}
                bookmark={<Bookmark itemId={videoData?.id} type={TYPE_VIDEO} />}
              />
            );
          })}
        </div>
        {paginationInfo?.pageCount > 1 && (
          <SecondaryPagination
            baseUrl={pathname}
            totalCount={paginationInfo?.total}
            pageSize={paginationInfo?.pageSize}
            currentPage={paginationInfo?.page}
            onPageChange={onPageChange}
            siblingCount={2}
            className={styles.pagination}
            pageNoSeparateRoute
            queryParams={queryParams}
          />
        )}
      </div>
      <FilterComponent
        resourceType={"video"}
        filters={filters}
        urlFilter={urlFilter}
        setIsFilterOpen={setIsFilterOpen}
        isFilterOpen={isFilterOpen}
        userAgent={ua}
      />
    </>
  );
};

export default VideoListingPage;
